import clsx from 'clsx';
import { useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { formatMpxc } from '@pxc/shared';
import { useLevelUpStore } from '../stores/levelup.store.js';
import { useNoticeStore } from '../stores/notice.store.js';
import { feedbackSound } from '../services/audio/feedback.js';
import { haptics } from '../services/telegram.js';
import { useT } from '../i18n/index.js';
import { Button } from './Button.js';
import { FitText } from './FitText.js';
import { PaintedTitle, hasPaintedTitle } from './PaintedTitle.js';

type CssVars = CSSProperties & Record<string, string | number>;

/*
 * The jump's beats, in milliseconds from the start: the same numbers as the
 * keyframes in levelup-artwork.css, which draw what these make heard and felt.
 */
const PUSH_MS = 260;
const PEAK_MS = 520;
const LAND_MS = 1290;
const READY_MS = 1700;

/** Sparks around the burst: where each settles (% of the stage), when it lights, how big. */
const SPARKS: [number, number, number, number][] = [
  [14, 14, 40, 1.8], [86, 12, 120, 1.5], [24, 27, 200, 1.2], [78, 26, 80, 2],
  [8, 38, 260, 1.3], [92, 40, 160, 1.6], [33, 8, 300, 1.1], [66, 7, 20, 1.3],
  [18, 52, 340, 1.5], [84, 55, 220, 1.2], [42, 31, 140, 1], [59, 33, 280, 1.1],
  [10, 66, 100, 1.2], [90, 68, 320, 1.4], [50, 4, 180, 1.6], [72, 46, 60, 1],
];

/** The dust a boot throws: how far out (cqw, + right), how far up, the spin, the size. */
const DUST: [number, number, number, number][] = [
  [-22, -5, -40, 2.6], [-15, -9, 25, 2], [-27, -1, 60, 1.8], [-9, -6, -15, 2.2],
  [22, -5, 40, 2.6], [15, -9, -25, 2], [27, -1, -60, 1.8], [9, -6, 15, 2.2],
];

/** Whole Gems without decimals, anything else to the hundredth. */
function gems(mpxc: number): string {
  return mpxc % 1000 === 0 ? formatMpxc(mpxc, { decimals: 0 }) : formatMpxc(mpxc);
}

/**
 * The level-up: the miner from the level-up sheet jumps for it — gets ready,
 * pushes off, rises, holds the peak under "¡NIVEL SUBIDO!", comes down,
 * lands in a puff of dust and cheers — then the new level and the way back.
 *
 * It waits for a notice on screen to be dismissed, so a reward is read before
 * the jump. A tap during the jump skips to its end; once it is over, a tap
 * (or Continue) closes it. With reduced motion it opens on the last pose.
 */
export function LevelUp() {
  const { t, locale } = useT();
  const current = useLevelUpStore((state) => state.current);
  const dismiss = useLevelUpStore((state) => state.dismiss);
  const noticeOpen = useNoticeStore((state) => state.current !== null);
  /** The jump is over (or was skipped): a tap now closes. */
  const [ready, setReady] = useState(false);
  /** Tapped through the jump: straight to its last pose. */
  const [skipped, setSkipped] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const shown = current && !noticeOpen ? current : null;

  useEffect(() => {
    setReady(false);
    setSkipped(false);
    setLeaving(false);
  }, [current?.id]);

  useEffect(() => {
    if (!shown) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      feedbackSound.success();
      haptics.notify('success');
      setReady(true);
      return undefined;
    }
    const timers = [
      window.setTimeout(() => haptics.impact('light'), PUSH_MS),
      window.setTimeout(() => {
        feedbackSound.success();
        haptics.notify('success');
      }, PEAK_MS),
      window.setTimeout(() => haptics.impact('medium'), LAND_MS),
      window.setTimeout(() => setReady(true), READY_MS),
    ];
    return () => timers.forEach((id) => window.clearTimeout(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown?.id]);

  const close = () => {
    if (leaving) return;
    setLeaving(true);
    window.setTimeout(() => {
      setLeaving(false);
      dismiss();
    }, 220);
  };

  const tap = () => {
    if (ready) {
      close();
      return;
    }
    setSkipped(true);
    setReady(true);
  };

  useEffect(() => {
    if (!shown) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') tap();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  if (!shown) return null;

  const title = t('levelUp.title');
  const level = t('levelUp.level', { level: shown.level });
  const painted = hasPaintedTitle('levelup', locale);

  return createPortal(
    <div
      key={shown.id}
      className={clsx('lvlup', ready && 'is-ready', skipped && 'is-skipped', leaving && 'is-leaving')}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} ${level}`}
      onClick={tap}
    >
      <div className="lvlup__glow" aria-hidden="true" />
      <div className="lvlup__sparks" aria-hidden="true">
        {SPARKS.map(([x, y, delay, size], index) => (
          <i key={index} style={{ '--x': `${x}%`, '--y': `${y}%`, '--d': `${delay}ms`, '--s': `${size}cqw` } as CssVars} />
        ))}
      </div>

      <div className="lvlup__stage">
        <div className={clsx('lvlup__head', painted && 'is-painted')}>
          {painted ? null : <span className="lvlup__rays" aria-hidden="true" />}
          <PaintedTitle name="levelup" label={title} className="lvlup__painted" as="span">
            <span className="lvlup__title">{title}</span>
          </PaintedTitle>
        </div>

        <div className="lvlup__hero-box" aria-hidden="true">
          <span className="lvlup__shadow" />
          {(['push', 'land'] as const).map((moment) => (
            <span key={moment} className={`lvlup__dust lvlup__dust--${moment}`}>
              {DUST.map(([dx, dy, spin, size], index) => (
                <i
                  key={index}
                  style={{ '--dx': `${dx}cqw`, '--dy': `${dy}cqw`, '--r': `${spin}deg`, '--s': `${size}cqw` } as CssVars}
                />
              ))}
            </span>
          ))}
          <span className="lvlup__hero" />
        </div>

        <div className="lvlup__badge">{level}</div>
        <p className="lvlup__next">
          {shown.nextLevelAtMpxc === null
            ? t('levelUp.max')
            : t('levelUp.next', { amount: gems(shown.nextLevelAtMpxc) })}
        </p>
        <Button
          className="lvlup__cta"
          onClick={(event) => {
            event.stopPropagation();
            close();
          }}
        >
          <FitText className="lvlup__cta-fit" min={0.5}>
            {t('levelUp.continue')}
          </FitText>
        </Button>
      </div>
    </div>,
    document.body,
  );
}
